import React, { useState, useContext, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { Pathname } from 'Routes';
import { AppContext, AuthContext } from 'Context';
import { dataFetcher, endpoints } from 'Api';
import logo from 'Assets/Login Logo.png';
import Right from 'Assets/small-right.png';
import Left from 'Assets/small-left.png';
import Plus from 'Assets/plus-button.svg';
import avatar01 from 'Assets/01.png';
import avatar02 from 'Assets/02.png';
import avatar03 from 'Assets/03.png';
import avatar04 from 'Assets/04.png';
import avatar05 from 'Assets/05.png';
import avatar06 from 'Assets/06.png';
import avatar07 from 'Assets/07.png';
import avatar08 from 'Assets/08.png';
import avatar09 from 'Assets/09.png';
import avatar10 from 'Assets/10.png';
import avatar11 from 'Assets/11.png';
import avatar12 from 'Assets/12.png';
import { Button } from 'Components';
import { Input } from '../components/input-field';
import styles from './index.module.css';

// Predefined avatar collection
const AVATAR_COLLECTION = [
  avatar11,
  avatar01,
  avatar02,
  avatar03,
  avatar04,
  avatar05,
  avatar06,
  avatar07,
  avatar08,
  avatar09,
  avatar10,
  avatar12,
];

export const Avatar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  console.log('location', location);
  const { setAppSnackbar, setUserDetails, userDetails, autoSignIn } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [selectedAvatar, setSelectedAvatar] = useState(userDetails?.dpURL ? null : avatar11);
  const [avatar, setAvatar] = useState(userDetails?.dpURL);
  const [previewImage, setPreviewImage] = useState(userDetails?.dpURL || avatar11);
  const [hasAvatarChanged, setHasAvatarChanged] = useState(false);
  const [firstName, setFirstName] = useState(userDetails?.firstname || '');
  const [lastName, setLastName] = useState(userDetails?.lastname || '');
  const [currentIndex, setCurrentIndex] = useState(0);

  // Carousel configuration
  const AVATARS_PER_VIEW = 4;
  const maxIndex = Math.max(0, AVATAR_COLLECTION.length - AVATARS_PER_VIEW);

  // Carousel navigation functions
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => Math.max(0, prevIndex - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => Math.min(maxIndex, prevIndex + 1));
  };

  // SWR hook for name updates
  const { isValidating: updatingName, mutate: updateName } = useSWR(
    firstName || lastName
      ? [endpoints.updateMyProfile, firstName, lastName]
      : null,
    {
      fetcher: (url, inputFirstname, inputLastname) => dataFetcher(url, {
          firstname: inputFirstname,
          lastname: inputLastname,
        }),
      onSuccess: ({ success }) => {
        if (success) {
          setAppSnackbar({
            open: true,
            message: 'Name was updated successfully',
            severity: 'success',
          });
          navigate(Pathname.authentication.toTrail);
        } else {
          setAppSnackbar({
            open: true,
            message: 'Failed to update profile names',
            severity: 'error',
          });
        }
      },
      onError: () => {
        setAppSnackbar({
          open: true,
          message: 'Oops! Something went wrong while updating name',
          severity: 'error',
        });
      },
    }
  );
  // SWR hook for avatar updates
  const { isValidating: updatingAvatar, mutate: updateAvatar } = useSWR(
    hasAvatarChanged ? [endpoints.updateProfileImge, avatar] : null,
    {
      fetcher: (url, inputAvatar) => {
        // Send the image with data URL prefix for the API
        const imageData = inputAvatar.includes('data:image')
          ? inputAvatar
          : `data:image/jpeg;base64,${inputAvatar}`;
        return dataFetcher(url, { image: imageData });
      },
      onSuccess: ({ success, image }) => {
        if (success) {
          setAppSnackbar({
            open: true,
            message: 'Avatar was updated successfully',
            severity: 'success',
          });
          setUserDetails({ ...userDetails, dpURL: image });
          autoSignIn();
          // Update names if provided after avatar success
          if (firstName || lastName) {
            updateName();
          } else {
            navigate(Pathname.authentication.toTrail);
          }
        } else {
          setAppSnackbar({
            open: true,
            message: 'Oops! Something went wrong while updating your avatar',
            severity: 'error',
          });
        }
      },
      onError: () => {
        setAppSnackbar({
          open: true,
          message: 'Oops! Something went wrong while updating your avatar',
          severity: 'error',
        });
      },
    }
  );

  // Convert image to base64
  const convertToBase64 = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setAppSnackbar({
          open: true,
          message: 'File size must be less than 5MB',
          severity: 'error',
        });
        return;
      }

      try {
        const base64 = await convertToBase64(file);
        setPreviewImage(base64);
        setSelectedAvatar(null);
        // Set avatar for SWR with full base64 data URL
        setAvatar(base64);
        setHasAvatarChanged(true);
      } catch (error) {
        setAppSnackbar({
          open: true,
          message: 'Error processing image',
          severity: 'error',
        });
      }
    }
  };
  // Auto-select avatar11 as default on component mount only if no existing dpURL
  useEffect(() => {
    const setDefaultAvatar = async () => {
      if (!userDetails?.dpURL) {
        try {
          const response = await fetch(avatar11);
          const blob = await response.blob();
          const base64 = await convertToBase64(blob);
          setAvatar(base64);
          setSelectedAvatar(avatar11); // Mark avatar11 as selected
          setHasAvatarChanged(true); // Need to update profile for users without dpURL
        } catch (error) {
          console.error('Error setting default avatar:', error);
        }
      }
    };
    setDefaultAvatar();
  }, []);
  // Handle avatar selection
  const handleAvatarSelect = async (avatarUrl) => {
    try {
      // Convert URL to base64
      const response = await fetch(avatarUrl);
      const blob = await response.blob();
      const base64 = await convertToBase64(blob);

      setSelectedAvatar(avatarUrl);
      setPreviewImage(avatarUrl);
      // Set avatar for SWR with full base64 data URL
      setAvatar(base64);
      setHasAvatarChanged(true);
    } catch (error) {
      setAppSnackbar({
        open: true,
        message: 'Error loading avatar',
        severity: 'error',
      });
    }
  };

  // Update names separately

  // Update profile using SWR
  const updateProfile = () => {
    if (!avatar) {
      setAppSnackbar({
        open: true,
        message: 'Please select an avatar or upload an image',
        severity: 'warning',
      });
      return;
    }

    // Only call avatar update API if user has changed the avatar
    if (hasAvatarChanged) {
      updateAvatar();
    } else if (firstName || lastName) {
      // If no avatar changes, just update names if provided
      updateName();
    } else {
      navigate(Pathname.authentication.toTrail);
    }
  };

  return (
    <div className={styles.avatarContainer}>
      {/* Background accents */}
      <div className={styles.bgOverlay}>
        <div className={styles.bgAccent1} />
        <div className={styles.bgAccent2} />
        <div className={styles.bgCircle} />
      </div>

      <div className={styles.contentWrapper}>
        {/* Header */}
        <div className={styles.header}>
          <Link to={Pathname.home}>
            <img src={logo} alt="Projector Logo" className={styles.logo} />
          </Link>
        </div>
        <div className={styles.titleWithPreview}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Choose Your Avatar</h1>
          <p className={styles.subtitle}>
            Select from our collection or upload your own
          </p>
        </div>
                    {/* Current Selection Preview */}
          {previewImage && (
            <div className={styles.previewContainer}>
              <div className={styles.previewAvatar}>
                <img
                  src={previewImage}
                  alt="Selected avatar"
                  className={styles.previewImage}
                />
              </div>
            </div>
          )}
        </div>

        {/* Avatar Selection Area */}
        <div className={styles.avatarSelectionArea}>
          {/* Name Fields - Hide if coming from verification code page */}
          {!location.state?.fromVerificationCode && (
            <div className={styles.nameFieldsContainer}>
              <div className={styles.nameFields}>
                <Input
                  automaticFocus
                  className={styles.customUserInput}
                  placeholder="First Name"
                  type="code"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  noLabel
                />
                <Input
                  className={styles.customUserInput}
                  placeholder="Last Name"
                  type="code"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  noLabel
                />
              </div>
            </div>
          )}

          {/* Avatar Carousel */}
          <div className={styles.collectionSection}>
            <div className={styles.carouselContainer}>
              <button
                className={styles.addButton}
                onClick={() => fileInputRef.current?.click()}
              >
                <img
                  src={Plus}
                  alt="add img"
                  className={styles.avatarThumbnail}
                />
              </button>
              <button
                className={styles.carouselNavButton}
                onClick={goToPrevious}
                disabled={currentIndex === 0}
              >
                <img src={Left} alt="Previous" />
              </button>
              <div className={styles.avatarCarousel}>
                <div
                  className={styles.avatarTrack}
                  style={{
                    transform: `translateX(-${
                      currentIndex * (100 / AVATARS_PER_VIEW)
                    }%)`,
                  }}
                >
                  {AVATAR_COLLECTION.map((avatar, index) => (
                    <button
                      key={index}
                      onClick={() => handleAvatarSelect(avatar)}
                      className={`${styles.avatarButton} ${
                        selectedAvatar === avatar
                          ? styles.avatarButtonSelected
                          : ''
                      }`}
                    >
                      <img
                        src={avatar}
                        alt={`Avatar ${index + 1}`}
                        className={styles.avatarThumbnail}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <button
                className={styles.carouselNavButton}
                onClick={goToNext}
                disabled={currentIndex >= maxIndex}
              >
                <img src={Right} alt="Next" />
              </button>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className={styles.hiddenInput}
          />

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <Button
              id="sasdasddd"
              blockText
              isBlue
              isDisabled={updatingAvatar || !avatar || !previewImage || !firstName.trim() || !lastName.trim()}
              isFullWidth
              isLarge
              onClick={updateProfile}
            >
              {updatingAvatar ? ' Updating...' : 'Continue'}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            onClick={() => navigate(Pathname.authentication.toTrail)}
            className={styles.skipButton}
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};
